<script lang="ts" setup>
import type { Placement, Strategy } from "@floating-ui/vue"
import type { IMenuItem } from "@/components/legacy/Menu/menu.interface"
import { autoUpdate, flip, shift, useFloating } from "@floating-ui/vue"
import { onClickOutside } from "@vueuse/core"
import { ref, useTemplateRef } from "vue"
import { MenuItem } from "@/components/legacy"

interface MenuProps {
  items?: IMenuItem[]
  placement?: Placement
  strategy?: Strategy
  disableTeleport?: boolean
}

const { items = [], placement = "bottom-start", strategy = "absolute" } = defineProps<MenuProps>()

const activatorRef = useTemplateRef<HTMLElement | null>("activatorRef")
const menuRef = useTemplateRef<HTMLElement | null>("menuRef")
const isOpen = ref<boolean>(false)

const { floatingStyles } = useFloating(activatorRef, menuRef, {
  placement,
  strategy,
  whileElementsMounted: autoUpdate,
  middleware: [flip(), shift()],
})

onClickOutside(
  menuRef,
  () => {
    isOpen.value = false
  },
  { ignore: [activatorRef] },
)

const toggle = () => (isOpen.value = !isOpen.value)

defineExpose({ toggle })

function onItemClick(item: IMenuItem) {
  if (item.dismissOnInteract)
    toggle()
}
</script>

<template>
  <div>
    <div ref="activatorRef" class="flex w-max">
      <slot name="activator" v-bind="{ toggle, isOpen }" />
    </div>
    <teleport to="body" :disabled="disableTeleport">
      <div
        v-show="isOpen" ref="menuRef" class="bg-white flex flex-col p-2 shadow-sm gap-2 rounded-xl z-50"
        :style="floatingStyles" data-test="menu"
      >
        <slot v-bind="{ toggle, isOpen }">
          <MenuItem
            v-for="(item, index) in items" :key="`menu-item-${index}`" :item="item"
            @item-click="() => onItemClick(item)"
          />
        </slot>
      </div>
    </teleport>
  </div>
</template>
